import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import logger from '@widgets/logger';
import { Typography } from '@components/Typography';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import Autocomplete from '@components/Autocomplete';
import PhoneInput from '@components/PhoneInput';
import { Checkbox } from '@components/Checkbox';
import TimeframePicker from '@components/TimeframePicker';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import BusinessHoursSelection from '@features/BusinessHoursSelection';
import SpecialHoursSelection from '@features/SpecialHoursSelection';
import { useTranslation, useLocations, usePermissions } from '@hooks';
import { KB, MB } from '@constants/file';
import { COMPANY_STATUS, MAX_SERVICES, paymentMethods } from '@constants/companies';
import { EDITABLE_FIELDS } from './formConfig';
import { Modal, Content } from './styles';
import { companyService } from '@services';
import { alertActions } from '@actions';
import { buildBulkUpdateRequest, bulkUpdateOutcome } from '@utils/locationBulkEditMapper';
import { descriptionContainsUrl } from '@helpers/locations';
import { handleApiError } from '@helpers/api';
import { SocialLinkField } from '../EditForm/SocialLinks';
import { SOCIAL_PLATFORMS, isValidSocialUrl } from '@utils/socialLinkUtils';
import { hasDuplicateSpecialDate, hasReversedSpecialRange } from '@helpers/companies';

const EMPTY_SOCIAL_LINKS = SOCIAL_PLATFORMS.reduce((acc, { key }) => ({ ...acc, [key]: '' }), {});
const SOCIAL_LINK_RULES = { validate: value => isValidSocialUrl(value) || 'validation.invalidURL' };
const SPECIAL_HOURS_RULES = {
    validate: {
        uniqueDates: value => !hasDuplicateSpecialDate(value) || 'validation.specialHoursDuplicateDate',
        forwardRanges: value => !hasReversedSpecialRange(value) || 'validation.specialHoursReversedRange',
    },
};

const StyledPhoneInput = styled(PhoneInput)({
    paddingBottom: 16,
    '& .error-message': {
        bottom: -14,
    },
});

const PaymentMethodsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: '4px 16px',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
    },
});

const MethodWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-7px)',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const FailureList = styled('ul')({
    margin: '4px 0 0',
    paddingLeft: 20,
});

const FieldContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
});

const BulkEditLocations = ({ open, onClose, onUpdated }) => {
    const { t } = useTranslation();
    const { getLocationsOptions } = useLocations();
    const { hasFullAccessAt } = usePermissions();
    const dispatch = useDispatch();

    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedField, setSelectedField] = useState(null);
    const [uploading, setUploading] = useState({ logoUrl: false, coverUrl: false });
    const [submitting, setSubmitting] = useState(false);
    const [outcome, setOutcome] = useState(null);
    const outcomeRef = useRef(null);
    const [phoneExtension, setPhoneExtension] = useState('');

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        unregister,
        formState: { isValid, isDirty },
    } = useForm({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {
            fieldValue: '',
            services: [{ name: '' }],
            paymentMethods: paymentMethods.map(item => ({ name: item.name, value: false })),
            workingHours: [],
            specialHours: [],
            openingDate: null,
            phoneNumber: '',
            status: '',
            socialLinks: { ...EMPTY_SOCIAL_LINKS },
        },
    });

    const {
        fields: serviceFields,
        append: appendService,
        remove: removeService,
    } = useFieldArray({
        control,
        name: 'services',
    });

    const locationsOptions = useMemo(
        () => getLocationsOptions(loc => hasFullAccessAt('locations', loc.companyId)),
        [getLocationsOptions, hasFullAccessAt],
    );
    const fieldValue = watch('fieldValue');
    const services = watch('services');
    const paymentMethodsValues = watch('paymentMethods');
    const workingHours = watch('workingHours');
    const specialHours = watch('specialHours');
    const openingDate = watch('openingDate');
    const phoneNumber = watch('phoneNumber');
    const status = watch('status');
    const logoUrl = watch('logoUrl');
    const coverUrl = watch('coverUrl');
    const socialLinks = watch('socialLinks');

    useEffect(() => {
        if (!open) {
            setSelectedLocations([]);
            setSelectedField(null);
            setPhoneExtension('');
            setSubmitting(false);
            setOutcome(null);
            reset();
        }
    }, [open, reset]);

    useEffect(() => {
        outcomeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [outcome]);

    const handleFieldChange = newField => {
        const fieldsToUnregister = [
            'fieldValue',
            'status',
            'phoneNumber',
            'openingDate',
            'workingHours',
            'specialHours',
            'services',
            'paymentMethods',
            'logoUrl',
            'coverUrl',
            'socialLinks',
        ];
        fieldsToUnregister.forEach(field => unregister(field));

        reset(
            {
                fieldValue: '',
                services: [{ name: '' }],
                paymentMethods: paymentMethods.map(item => ({ name: item.name, value: false })),
                workingHours: [],
                specialHours: [],
                openingDate: null,
                phoneNumber: '',
                status: '',
                logoUrl: null,
                coverUrl: null,
                socialLinks: { ...EMPTY_SOCIAL_LINKS },
            },
            { keepErrors: false },
        );

        setPhoneExtension('');
        setSelectedField(newField);
        setOutcome(null);
    };

    const handleLocationChange = items => {
        setSelectedLocations(items || []);
    };

    const uploadImage = (e, field) => async () => {
        if (!selectedLocations || selectedLocations.length === 0) {
            dispatch(alertActions.error(t('BulkEditLocations.selectLocationsFirst')));
            e.target.value = '';
            return;
        }

        try {
            setUploading(prev => ({ ...prev, [field]: true }));

            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);

            const type = field === 'logoUrl' ? 'logo' : 'cover';
            const response = await companyService.uploadCompanyImage(data, selectedLocations[0].id, type);

            setValue(field, response.publicUrl, { shouldDirty: true, shouldValidate: true });
        } catch (error) {
            logger.error(`Couldn't upload ${field}`, error);
            dispatch(alertActions.error(t('BulkEditLocations.uploadError')));
        } finally {
            setUploading(prev => ({ ...prev, [field]: false }));
            e.target.value = '';
        }
    };

    const onDeleteImage = field => {
        setValue(field, null, { shouldDirty: true, shouldValidate: true });
    };

    const getCurrentFieldValue = () => {
        if (!selectedField) return null;

        const fieldConfig = EDITABLE_FIELDS.find(f => f.value === selectedField.value);
        if (fieldConfig?.component === 'SocialLink') {
            return socialLinks?.[fieldConfig.platform] ?? '';
        }

        // Map form field values to their corresponding watched values
        const fieldValueMap = {
            services: services,
            paymentMethods: paymentMethodsValues
                .filter(method => method.value)
                .map(method => paymentMethods.find(pm => pm.name === method.name)?.value),
            workingHours: workingHours,
            specialHours: specialHours,
            openingDate: openingDate,
            phoneNumber: { number: phoneNumber, extension: phoneExtension },
            status: status,
            logoUrl: logoUrl,
            coverUrl: coverUrl,
        };

        return fieldValueMap[selectedField.value] ?? fieldValue;
    };

    const onSubmit = async () => {
        const currentValue = getCurrentFieldValue();
        const request = buildBulkUpdateRequest(
            selectedLocations.map(loc => loc.id),
            selectedField.value,
            currentValue,
        );

        try {
            setSubmitting(true);
            setOutcome(null);
            const result = bulkUpdateOutcome(await companyService.bulkUpdateLocations(request), selectedLocations, t);
            if (result.updatedAny) {
                onUpdated();
            }
            if (result.complete) {
                dispatch(alertActions.success(t('alertMessages.updateSuccess')));
                onClose();
                return;
            }
            const retryableIds = new Set(
                result.failed.filter(failure => failure.retryable).map(failure => failure.locationId),
            );
            setSelectedLocations(current => current.filter(location => retryableIds.has(location.id)));
            setOutcome(result);
        } catch (error) {
            logger.error('Failed to bulk update locations', error);
            handleApiError(error, 'alertMessages.updateFail');
        } finally {
            setSubmitting(false);
        }
    };

    const renderFieldInput = () => {
        if (!selectedField) return null;

        const fieldConfig = EDITABLE_FIELDS.find(f => f.value === selectedField.value);

        switch (fieldConfig.component) {
            case 'Input':
                if (fieldConfig.value === 'shortDescription') {
                    return (
                        <Controller
                            control={control}
                            name="fieldValue"
                            rules={{
                                maxLength: {
                                    value: 75,
                                    message: t('validation.maxLength', { count: 75 }),
                                },
                                validate: value =>
                                    !descriptionContainsUrl(value) || t('validation.descriptionContainsUrl'),
                            }}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.descriptionShort')}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxLength={75}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                />
                            )}
                        />
                    );
                } else if (fieldConfig.value === 'mediumDescription') {
                    return (
                        <Controller
                            control={control}
                            name="fieldValue"
                            rules={{
                                maxLength: {
                                    value: 500,
                                    message: t('validation.maxLength', { count: 500 }),
                                },
                                validate: value =>
                                    !descriptionContainsUrl(value) || t('validation.descriptionContainsUrl'),
                            }}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.descriptionMedium')}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxLength={500}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                />
                            )}
                        />
                    );
                } else if (fieldConfig.value === 'longDescription') {
                    return (
                        <Controller
                            control={control}
                            name="fieldValue"
                            rules={{
                                maxLength: {
                                    value: 750,
                                    message: t('validation.maxLength', { count: 750 }),
                                },
                                validate: value =>
                                    !descriptionContainsUrl(value) || t('validation.descriptionContainsUrl'),
                            }}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.descriptionLong')}
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    maxLength={750}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                />
                            )}
                        />
                    );
                } else if (fieldConfig.value === 'locationName') {
                    return (
                        <Controller
                            control={control}
                            name="fieldValue"
                            rules={{ required: t('validation.required') }}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.locationName')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    required
                                />
                            )}
                        />
                    );
                } else if (fieldConfig.value === 'email') {
                    return (
                        <Controller
                            control={control}
                            name="fieldValue"
                            rules={{
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('validation.wrongEmailFormat'),
                                },
                            }}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.email')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                />
                            )}
                        />
                    );
                } else if (fieldConfig.value === 'website') {
                    return (
                        <Controller
                            control={control}
                            name="fieldValue"
                            rules={{
                                pattern: {
                                    value: /^https?:\/\/.+/i,
                                    message: t('validation.invalidURL'),
                                },
                            }}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.website')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                />
                            )}
                        />
                    );
                }
                return null;

            case 'Select': {
                const statusOptions = [
                    { value: COMPANY_STATUS.OPEN, label: t('common.open') },
                    { value: COMPANY_STATUS.CLOSED, label: t('common.closed') },
                ];
                return (
                    <Controller
                        control={control}
                        name="status"
                        rules={{ required: t('validation.required') }}
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                onChange={({ target: { value } }) => {
                                    setValue('status', value, { shouldDirty: true, shouldValidate: true });
                                }}
                                options={statusOptions}
                                label={t('common.status')}
                                fullWidth
                                error={!!error}
                                helperText={error ? error.message : null}
                                required
                            />
                        )}
                    />
                );
            }

            case 'PhoneInput':
                return (
                    <FieldContainer>
                        <Controller
                            control={control}
                            name="phoneNumber"
                            rules={{
                                required: t('validation.required'),
                                validate: value => {
                                    if (!value || value.length < 10) {
                                        return t('validation.wrongPhoneFormat');
                                    }
                                    return true;
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <StyledPhoneInput
                                    placeholder=""
                                    label={t('SettingsCompanies.primaryPhone')}
                                    inputProps={{ name: 'phoneNumber' }}
                                    value={field.value}
                                    autoFormat
                                    onChange={value => {
                                        setValue('phoneNumber', value, { shouldValidate: true, shouldDirty: true });
                                    }}
                                    error={error ? error.message : null}
                                    required
                                />
                            )}
                        />
                        <Input
                            inputProps={{
                                value: phoneExtension,
                                onChange: e => setPhoneExtension(e.target.value),
                            }}
                            label={t('SettingsCompanies.phoneExtension')}
                            fullWidth
                        />
                    </FieldContainer>
                );

            case 'TimeframePicker':
                return (
                    <Controller
                        control={control}
                        name="openingDate"
                        render={({ field }) => (
                            <TimeframePicker
                                value={field.value ? [field.value] : [null]}
                                range={false}
                                label={t('Listings.openingDate')}
                                onChangeTimeframe={value => {
                                    field.onChange(value);
                                    setValue('openingDate', value, { shouldDirty: true, shouldValidate: true });
                                }}
                                minDate={null}
                                maxDate={null}
                            />
                        )}
                    />
                );

            case 'BusinessHoursSelection':
                return (
                    <Controller
                        control={control}
                        name="workingHours"
                        render={({ field }) => (
                            <BusinessHoursSelection
                                data={field.value}
                                withAdditionalTime
                                onChangeHours={value => {
                                    field.onChange(value);
                                    setValue('workingHours', value, { shouldDirty: true, shouldValidate: true });
                                }}
                            />
                        )}
                    />
                );

            case 'SpecialHoursSelection':
                return (
                    <Controller
                        control={control}
                        name="specialHours"
                        rules={SPECIAL_HOURS_RULES}
                        render={({ field, fieldState: { error } }) => (
                            <>
                                <SpecialHoursSelection
                                    data={field.value}
                                    onChange={value => {
                                        field.onChange(value);
                                        setValue('specialHours', value, { shouldDirty: true, shouldValidate: true });
                                    }}
                                />
                                {error?.message && (
                                    <Typography variant="caption" color="error">
                                        {t(error.message)}
                                    </Typography>
                                )}
                            </>
                        )}
                    />
                );

            case 'ServicesArray':
                return (
                    <div style={{ display: 'flex', gap: '24px', flexFlow: 'column' }}>
                        {serviceFields.map((field, index) => (
                            <FieldWithActions
                                key={field.id}
                                actions={rowActions({
                                    index,
                                    count: serviceFields.length,
                                    onAdd: () => appendService({ name: '' }),
                                    onDelete: () => removeService(index),
                                    canAdd: serviceFields.length < MAX_SERVICES,
                                    t,
                                })}
                            >
                                <Controller
                                    control={control}
                                    name={`services.${index}.name`}
                                    render={({ field: formField, ref }) => (
                                        <Input
                                            ref={ref}
                                            inputProps={{
                                                ...formField,
                                                onChange: e => formField.onChange(e.target.value),
                                            }}
                                            label={`${t('common.service')} ${index + 1}`}
                                        />
                                    )}
                                />
                            </FieldWithActions>
                        ))}
                    </div>
                );

            case 'SocialLink': {
                const platform = SOCIAL_PLATFORMS.find(p => p.key === fieldConfig.platform);
                return platform ? (
                    <SocialLinkField platform={platform} control={control} rules={SOCIAL_LINK_RULES} />
                ) : null;
            }

            case 'CheckboxGroup':
                return (
                    <PaymentMethodsGrid>
                        {paymentMethods.map((method, index) => (
                            <MethodWrapper key={method.name}>
                                <Controller
                                    control={control}
                                    name={`paymentMethods.${index}.value`}
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue(`paymentMethods.${index}.value`, e.target.checked, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t(`paymentMethod.${method.name}`)}</Typography>
                            </MethodWrapper>
                        ))}
                    </PaymentMethodsGrid>
                );

            case 'ImageUploaderSingle': {
                const isLogo = fieldConfig.imageType === 'logo';
                const imageField = isLogo ? 'logoUrl' : 'coverUrl';
                const imageValue = isLogo ? logoUrl : coverUrl;

                return (
                    <FieldContainer>
                        <Typography variant="body2">
                            {isLogo ? t('common.logo') : t('SettingsCompanies.coverPhoto')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--text-secondary)', display: 'block', margin: '4px 0 8px' }}
                        >
                            {t('common.imageUploadFormat', { size: isLogo ? '500KB' : '5MB' })}
                        </Typography>
                        <Controller
                            control={control}
                            name={imageField}
                            render={() => (
                                <ImageUploaderSingle
                                    maxSize={isLogo ? KB * 500 : 5 * MB}
                                    minSize={10 * KB}
                                    extensions="image/*"
                                    cropConfig={
                                        isLogo
                                            ? { aspectRatio: 1, outputWidth: 250 }
                                            : { aspectRatio: 16 / 9, outputMinWidth: 1110, outputMaxWidth: 2120 }
                                    }
                                    logoUrl={imageValue}
                                    disabled={uploading[imageField]}
                                    onDelete={() => onDeleteImage(imageField)}
                                    uploadLogo={e => uploadImage(e, imageField)()}
                                    isUploading={uploading[imageField]}
                                />
                            )}
                        />
                    </FieldContainer>
                );
            }

            default:
                return null;
        }
    };

    const selectedFieldConfig = selectedField ? EDITABLE_FIELDS.find(f => f.value === selectedField.value) : null;
    // Bulk edit sets a value; like every other field here it can't clear. Requiring a non-empty social value
    // also prevents an accidental clear when the form is only dirtied by toggling the sync switch.
    const hasRequiredSocialValue =
        selectedFieldConfig?.component !== 'SocialLink' || !!socialLinks?.[selectedFieldConfig.platform]?.trim();

    const isFormValid = selectedLocations.length > 0 && selectedField && isDirty && isValid && hasRequiredSocialValue;
    const buttonText = submitting
        ? t('common.saving')
        : t('BulkEditLocations.applyToLocations', { count: selectedLocations.length });

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={onClose}
            isDirty={isDirty}
            title={t('BulkEditLocations.title')}
            subtitle={t('BulkEditLocations.subtitle')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={buttonText}
            primaryActionDisabled={!isFormValid || submitting}
        >
            <Content>
                {outcome && (
                    <Alert severity="error" ref={outcomeRef}>
                        <Typography variant="body2">
                            {t('BulkEditLocations.notUpdated', { count: outcome.failed.length, total: outcome.total })}
                        </Typography>
                        <FailureList>
                            {outcome.failed.map(failure => (
                                <li key={failure.locationId}>
                                    <Typography variant="body2">
                                        {failure.name}: {failure.reason}
                                    </Typography>
                                </li>
                            ))}
                        </FailureList>
                    </Alert>
                )}
                <Autocomplete
                    label={t('common.locations')}
                    options={locationsOptions}
                    value={selectedLocations}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChangeAutoComplete={handleLocationChange}
                    groupMultiProps={{
                        getOptionLabel: option => option.label,
                    }}
                    required
                />

                <Select
                    label={t('InboxPage.selectProperty')}
                    options={EDITABLE_FIELDS.map(field => ({
                        value: field.value,
                        label: t(field.label, field.labelParams),
                    }))}
                    value={selectedField?.value || ''}
                    onChange={e => {
                        const field = EDITABLE_FIELDS.find(f => f.value === e.target.value);
                        handleFieldChange(field);
                    }}
                    fullWidth
                    placeholder={t('InboxPage.selectProperty')}
                />

                {/* Key prop forces React to remount component when field changes, ensuring complete isolation */}
                {selectedField && <div key={selectedField.value}>{renderFieldInput()}</div>}
            </Content>
        </Modal>
    );
};

export default BulkEditLocations;
